import { Inject, Injectable, OnDestroy, Optional } from '@angular/core';
import { SubscriptionResult } from 'apollo-angular';
import {
	BehaviorSubject,
	Observable,
	ReplaySubject,
	Subject,
	Subscriber,
	Subscription,
	from,
	map,
	merge,
	retry,
	skip,
	takeUntil,
	tap,
	timer
} from 'rxjs';

import {
	CombiningSubscriptionManager,
	NodeChangeType,
	SubscriptionManagerService
} from '@shure/shared/angular/data-access/system-api/core';
import { mapDeviceModelFromSysApi } from '@shure/shared/angular/data-access/system-api/mappers';
import { DeviceStatus } from '@shure/shared/angular/data-access/system-api/models';
import { interfaceIdToFormattedVersion } from '@shure/shared/angular/services';
import { AnalyticsService } from '@shure/shared/angular/utils/analytics';
import { ILogger } from '@shure/shared/angular/utils/logging';
import { throttledBuffer, tapOnce } from '@shure/shared/typescript/rxjs';

import {
	DeviceDiscoveredAnalyticsEvent,
	DeviceOnlineAnalyticsEvent,
	DEVICE_ANALYTICS_EVENTS_CONFIG,
	DeviceAnalyticsEventsConfig
} from '../analytics';
import {
	LiveDeviceDiscoveryApiService,
	IS_DISCOVERABLE_VIRTUAL_DEVICE,
	IsDeviceDiscoverableFilter
} from '../api/live-device-discovery-api.service';
import {
	DeviceDiscoveryDevice,
	DeviceDiscoveryEvent,
	DeviceDiscoveryTransactions
} from '../api/models/device-discovery.models';

import {
	DiscoverDevicesQueryGQL,
	DiscoveryDeviceFragment,
	DiscoveryDeviceSubscriptionGQL,
	DiscoveryDeviceSubscriptionOpResult,
	DiscoveryDevicesSubscriptionGQL,
	DiscoveryOnlineDeviceFragment
} from './graphql/generated/discover-devices.generated';

function mapDeviceDiscoverDevice(device: DiscoveryDeviceFragment): DeviceDiscoveryDevice {
	return {
		id: device.id,
		status: device.status,
		model: mapDeviceModelFromSysApi(device.advertisedInterface.model ?? device.description.model),
		dmpIpAddress: device.protocol.dmp?.ipAddress,
		hardwareId: device.hardwareId,
		originHardwareId: device.features.virtual?.origin?.hardwareId,
		interfaceId: device.description.interface.version.interfaceId,
		virtual: device.virtual
	};
}

function mapOnlineDevice(device: DiscoveryOnlineDeviceFragment): DeviceDiscoveryDevice {
	return {
		...mapDeviceDiscoverDevice(device),
		firmwareVersion: device.features.firmware?.version ?? ''
	};
}

@Injectable()
export class LiveDeviceDiscoverySysApiService implements LiveDeviceDiscoveryApiService, OnDestroy {
	private readonly logger: ILogger;
	private readonly destroy$ = new Subject<void>();
	private readonly trackedOnlineDeviceIds = new Set<string>();

	private discoveredDevices = new Map<string, DeviceDiscoveryDevice>();
	private _discoveryEvents$ = new Subject<DeviceDiscoveryEvent>();
	private _discoveredDevices$ = new BehaviorSubject<DeviceDiscoveryDevice[]>([]);
	private _discoveredDeviceIdForAnalytics$ = new ReplaySubject<string>(Infinity);
	private _onlineDeviceIdForAnalytics$ = new ReplaySubject<string>(Infinity);

	private readonly deviceSubscriptionManager: CombiningSubscriptionManager<DiscoveryDeviceSubscriptionOpResult>;

	constructor(
		logger: ILogger,
		private readonly discoveryDevicesSubscriptionGQL: DiscoveryDevicesSubscriptionGQL,
		private readonly discoverDevicesQueryGQL: DiscoverDevicesQueryGQL,
		discoveryDeviceSubscriptionGQL: DiscoveryDeviceSubscriptionGQL,
		private readonly analyticsService: AnalyticsService,
		subscriptionManagerService: SubscriptionManagerService,
		@Inject(DEVICE_ANALYTICS_EVENTS_CONFIG) private deviceAnalyticsEventsConfig: DeviceAnalyticsEventsConfig,
		@Optional()
		@Inject(IS_DISCOVERABLE_VIRTUAL_DEVICE)
		private isDiscoverableVirtualDevice: IsDeviceDiscoverableFilter
	) {
		this.logger = logger.createScopedLogger('LiveDeviceDiscoverySysApiService');

		this.deviceSubscriptionManager = new CombiningSubscriptionManager<DiscoveryDeviceSubscriptionOpResult>(
			{
				name: 'LiveDeviceDiscoverySysApiService',
				// DNG-9721, Invalid device count. Due to a racecondition in system-api related to the rapid change between
				// discovered/online state, we have added this changeType subscription which ensure that a proper
				// subscription is added between system-api and phx. We do not need the name here.
				changeTypes: [NodeChangeType.DeviceName],
				subscriptionGql: discoveryDeviceSubscriptionGQL,
				onUpdate: this.handleUpdateFromDeviceSubscription.bind(this)
			},
			logger,
			subscriptionManagerService
		);

		this.start();
	}

	public ngOnDestroy(): void {
		this.deviceSubscriptionManager.unsubscribeAll();
		this.destroy$.next();
		this.destroy$.complete();
	}

	public getDiscoveryEvents$(): Observable<DeviceDiscoveryEvent> {
		return merge(
			this._discoveryEvents$,
			from([...this.discoveredDevices.values()].map((device) => ({ added: device })))
		);
	}

	public getOnlineDeviceCount$(): Observable<number> {
		return this._discoveredDevices$.pipe(
			map((devices) => {
				return devices.filter((d) => d.status === DeviceStatus.Online).length;
			})
		);
	}

	public getDetectedDeviceCount$(): Observable<number> {
		return this._discoveredDevices$.pipe(
			map((devices) => {
				return devices.length;
			})
		);
	}

	public getDiscoveryDeviceIdForAnalytics$(): Observable<string> {
		return this._discoveredDeviceIdForAnalytics$.asObservable();
	}

	public getOnlineDeviceIdForAnalytics$(): Observable<string> {
		return this._onlineDeviceIdForAnalytics$.asObservable();
	}

	public getDiscoveredDevicesTransactionsByQuery$<ElementType>(
		elementQueryFunction: (id: string) => Observable<ElementType>,
		config?: {
			throttleMs?: number;
			deviceIdFilterFunction?: (id: string) => boolean;
		}
	): Observable<DeviceDiscoveryTransactions<ElementType>> {
		return new Observable((observer: Subscriber<DeviceDiscoveryTransactions<ElementType>>) => {
			try {
				const throttleMs = config?.throttleMs ?? 0;
				const eventStream$ =
					throttleMs !== 0
						? this.getDiscoveryEvents$().pipe(throttledBuffer(throttleMs))
						: this.getDiscoveryEvents$();
				const queryHandlers = new Map<string, Subscription>();
				const eventStreamSubscription = eventStream$
					.pipe(
						tap((e) => {
							if ('added' in e && e.added?.id) {
								// if deviceIdFilterFunction is used and device ID doesn't pass filter, don't execute the query function
								if (config?.deviceIdFilterFunction && !config.deviceIdFilterFunction(e.added.id)) {
									return;
								}
								const queryHandler = elementQueryFunction(e.added.id)
									.pipe(
										tapOnce((d) => {
											observer.next({ add: [d], remove: [], update: [] });
										}),
										skip(1)
									)
									.subscribe((d) => {
										observer.next({ add: [], remove: [], update: [d] });
									});
								queryHandlers.set(e.added.id, queryHandler);
							}
						})
					)
					.subscribe();
				observer.add(() => {
					queryHandlers.forEach((queryHandler) => queryHandler.unsubscribe());
					eventStreamSubscription.unsubscribe();
				});
			} catch (_error) {
				observer.error();
			}
		});
	}

	public getDiscoveredDevices(): DeviceDiscoveryDevice[] {
		return Array.from(this.discoveredDevices.values());
	}

	public getDiscoveredDevicesByQuery$<ElementType extends { id: string }>(
		elementQueryFunction: (id: string) => Observable<ElementType>,
		config?: {
			throttleMs?: number;
		}
	): Observable<ElementType[]> {
		return new Observable((observer: Subscriber<ElementType[]>) => {
			observer.next([]); // Start by emitting empty array in case no devices that pass the filter exist
			try {
				const devices = new Map<string, ElementType>();
				const queryHandlers = new Map<string, Subscription>();
				const throttleMs = config?.throttleMs ?? 0;
				const eventStream$ =
					throttleMs !== 0
						? this.getDiscoveryEvents$().pipe(throttledBuffer(throttleMs))
						: this.getDiscoveryEvents$();
				const eventStreamSubscription = eventStream$
					.pipe(
						tap((e) => {
							if ('added' in e && e.added?.id) {
								const queryHandler = elementQueryFunction(e.added.id).subscribe((d) => {
									devices.set(d.id, d);
									observer.next([...devices.values()]);
								});
								queryHandlers.set(e.added.id, queryHandler);
							}
						})
					)
					.subscribe((e) => {
						if ('removed' in e && e.removed) {
							const queryHandler = queryHandlers.get(e.removed);
							if (queryHandler) {
								queryHandler.unsubscribe();
								queryHandlers.delete(e.removed);
							}
							devices.delete(e.removed);
							observer.next([...devices.values()]);
						}
					});
				observer.add(() => {
					eventStreamSubscription.unsubscribe();
					queryHandlers.forEach((queryHandler) => queryHandler.unsubscribe());
				});
			} catch (_error) {
				observer.error();
			}
		});
	}

	private start(): void {
		this.logger.trace('start()', 'Starting discovery');
		this.discoveryDevicesSubscriptionGQL
			.subscribe()
			.pipe(
				retry({
					delay: (_error, retryCount: number) => {
						// Exponential backoff
						return timer(retryCount * retryCount * 1000);
					},
					resetOnSuccess: true
				}),
				takeUntil(this.destroy$)
			)
			.subscribe((response) => {
				const event = response.data?.discoveredDevices;
				if (event) {
					if ('added' in event) {
						const device = mapDeviceDiscoverDevice(event.added);
						this.handleDeviceDiscovered(device);
					}

					// Currently we keep all live devices to hold all offline devices in the list
					if ('removed' in event) {
						this.removeDevice(event.removed);
					}
				}
			});
		this.fetchDiscoveredDevices();
		setTimeout(() => this.fetchDiscoveredDevices(), 2000);
	}

	private handleDeviceDiscovered(device: DeviceDiscoveryDevice): void {
		if (device.virtual && !this.isDiscoverableVirtualDevice?.(device)) {
			// Some applications may use this device ID for analytics even if the device is not discoverable
			// Virtual devices may not transition from discovered to online so we need to track both here
			this._discoveredDeviceIdForAnalytics$.next(device.id);
			this._onlineDeviceIdForAnalytics$.next(device.id);
			return;
		}

		if (!this.discoveredDevices.has(device.id)) {
			if (this.deviceAnalyticsEventsConfig.includeDiscoveryEventsAnalytics) {
				this.trackDeviceDiscoveredEvents(device, false);
			} else {
				this._discoveredDeviceIdForAnalytics$.next(device.id);
			}
			this.trackedOnlineDeviceIds.delete(device.id);
		}

		this.addDevice(device);
	}

	private addDevice(device: DeviceDiscoveryDevice): void {
		if (this.discoveredDevices.has(device.id)) {
			this.updateDevice(device.id, device);
		} else {
			this.logger.trace('addDevice()', 'Adding device', { device });

			this.deviceSubscriptionManager.subscribe('all', device.id); // Do not prune subscription manager
			this._discoveryEvents$.next({ added: device });
			this.discoveredDevices.set(device.id, device);
			this.updateDiscoveredDevices();
		}
	}

	private removeDevice(id: string): void {
		const device = this.discoveredDevices.get(id);

		if (device) {
			this.logger.trace('removeDevice()', 'Removing device', { id });
			this.deviceSubscriptionManager.unsubscribe('all', id);
			this._discoveryEvents$.next({ removed: id });
			this.discoveredDevices.delete(id);
			this.updateDiscoveredDevices();
		} else {
			this.logger.warning('removeDevice()', 'Device not found in inventory', { id });
		}
	}

	private updateDevice(id: string, deviceUpdate: Partial<DeviceDiscoveryDevice>): DeviceDiscoveryDevice | null {
		this.logger.trace('updateDevice()', 'Updating device', { id, deviceUpdate });

		const device = this.discoveredDevices.get(id);
		if (!device) {
			this.logger.error('updateDevice()', 'Device not found in inventory', { id, deviceUpdate });
			return null;
		}

		const updatedDevice = { ...device, ...deviceUpdate };
		this._discoveryEvents$.next({ updated: updatedDevice });
		this.discoveredDevices.set(id, updatedDevice);
		this.updateDiscoveredDevices();

		return updatedDevice;
	}

	private updateDiscoveredDevices(): void {
		this._discoveredDevices$.next([...this.discoveredDevices.values()]);
	}

	private handleUpdateFromDeviceSubscription(
		_targetId: string,
		change: SubscriptionResult<DiscoveryDeviceSubscriptionOpResult>
	): void {
		if (change.data && 'isDeviceStatusChange' in change.data.nodeChanges) {
			this.logger.trace('deviceSubscription.change', 'DeviceStatusChange detected', {
				id: change.data.nodeChanges.id
			});

			const deviceUpdate = {
				status: change.data.nodeChanges.status
			};
			this.handleLiveDeviceChange(change.data.nodeChanges.id, deviceUpdate);
		} else if (change.data && 'isDevice' in change.data.nodeChanges) {
			this.logger.trace('deviceSubscription.change', 'Device detected', {
				id: change.data.nodeChanges.id,
				status: change.data.nodeChanges.status
			});

			const deviceUpdate = mapOnlineDevice(change.data.nodeChanges);
			this.handleLiveDeviceChange(deviceUpdate.id, deviceUpdate);
		}
	}

	private handleLiveDeviceChange(id: string, deviceUpdate: Partial<DeviceDiscoveryDevice>): void {
		const updatedDevice = this.updateDevice(id, deviceUpdate);

		if (updatedDevice?.status === DeviceStatus.Online) {
			if (!this.trackedOnlineDeviceIds.has(id)) {
				if (this.deviceAnalyticsEventsConfig.includeDiscoveryEventsAnalytics) {
					this.trackDeviceOnlineEvents(updatedDevice, false);
				} else {
					this._onlineDeviceIdForAnalytics$.next(updatedDevice.id);
				}
				this.trackedOnlineDeviceIds.add(id);
			}
		} else {
			this.trackedOnlineDeviceIds.delete(id);
		}
	}

	private trackDeviceDiscoveredEvents(device: DeviceDiscoveryDevice, isVirtual: boolean): void {
		this.analyticsService.trackEvent(
			new DeviceDiscoveredAnalyticsEvent(
				device.hardwareId,
				interfaceIdToFormattedVersion(device.interfaceId),
				device.model,
				isVirtual
			)
		);
	}

	private trackDeviceOnlineEvents(device: DeviceDiscoveryDevice, isVirtual: boolean): void {
		this.analyticsService.trackEvent(
			new DeviceOnlineAnalyticsEvent(
				device.hardwareId,
				interfaceIdToFormattedVersion(device.interfaceId),
				device.model,
				isVirtual,
				device.firmwareVersion
			)
		);
	}

	private fetchDiscoveredDevices(): void {
		this.discoverDevicesQueryGQL
			.fetch({}, { fetchPolicy: 'network-only', errorPolicy: 'all' })
			.pipe(takeUntil(this.destroy$))
			.subscribe((response) => {
				if (response.data) {
					response.data.discoveredDevicesConnection.edges
						.map((edge) => edge.node)
						.filter(
							(deviceFragment): deviceFragment is DiscoveryOnlineDeviceFragment => deviceFragment != null
						)
						.forEach((deviceFragment) => {
							const device = mapOnlineDevice(deviceFragment);
							this.handleDeviceDiscovered(device);
						});
				}
				if (response.error) {
					this.logger.warning('start()', 'An error was included in device discovery query', {
						error: response.error
					});
				}
			});
	}
}
